import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Loader2, RefreshCw, AlertTriangle } from 'lucide-react';
import { diagnosticsAPI } from '../../lib/api';

export function InvoiceCurrencyBackfillPanel() {
  const [preview, setPreview] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);

  const runPreview = async () => {
    setPreviewLoading(true);
    setApplyResult(null);
    try {
      const res = await diagnosticsAPI.invoiceCurrencyBackfillPreview();
      setPreview(res.data);
    } catch {
      setPreview({ error: 'Failed to run preview.' });
    } finally {
      setPreviewLoading(false);
    }
  };

  const runApply = async () => {
    if (!window.confirm(
      `Apply currency corrections to ${preview?.corrections_needed || 0} invoices? ` +
      'This corrects the currency label only (amounts are never modified).'
    )) return;
    setApplyLoading(true);
    try {
      const res = await diagnosticsAPI.invoiceCurrencyBackfillApply();
      setApplyResult(res.data);
    } catch {
      setApplyResult({ error: 'Failed to apply corrections.' });
    } finally {
      setApplyLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <RefreshCw className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">Invoice Currency Backfill</CardTitle>
        </div>
        <p className="text-xs text-muted-foreground">
          Re-reads CurrencyCode from Xero and corrects the stored currency label.
          Does NOT modify amounts. Two-step: Preview, then Apply.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Preview button */}
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={runPreview}
            disabled={previewLoading}
          >
            {previewLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
            Preview
          </Button>
          {preview && !preview.error && preview.corrections_needed > 0 && (
            <Button
              variant="default"
              size="sm"
              onClick={runApply}
              disabled={applyLoading || !!applyResult}
            >
              {applyLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Apply {preview.corrections_needed} Corrections
            </Button>
          )}
        </div>

        {/* Preview results */}
        {preview && (
          <>
            {preview.error ? (
              <p className="text-xs text-red-600">{preview.error}</p>
            ) : (
              <div className="space-y-2">
                <div className="flex flex-wrap gap-3 text-xs">
                  <span>Checked: <strong>{preview.checked}</strong></span>
                  <span>
                    Corrections needed:{' '}
                    <strong className={preview.corrections_needed > 0 ? 'text-amber-600' : ''}>
                      {preview.corrections_needed}
                    </strong>
                  </span>
                  <span>Already correct: <strong>{preview.already_correct}</strong></span>
                  {preview.amount_flags_for_manual_review > 0 && (
                    <span className="text-red-600">
                      Amount mismatches (manual review): <strong>{preview.amount_flags_for_manual_review}</strong>
                    </span>
                  )}
                </div>

                {/* Correction patterns */}
                {preview.correction_patterns && Object.keys(preview.correction_patterns).length > 0 && (
                  <div className="text-xs">
                    <span className="font-medium">Patterns: </span>
                    {Object.entries(preview.correction_patterns).map(([pattern, count]) => (
                      <Badge key={pattern} variant="outline" className="text-[10px] mr-1">
                        {pattern}: {count}
                      </Badge>
                    ))}
                  </div>
                )}

                {/* Correction samples */}
                {preview.corrections?.length > 0 && (
                  <div className="border rounded overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          <th className="text-left px-2 py-1 font-medium">Invoice #</th>
                          <th className="text-left px-2 py-1 font-medium">Supplier</th>
                          <th className="text-left px-2 py-1 font-medium">Entity</th>
                          <th className="text-center px-2 py-1 font-medium">Stored</th>
                          <th className="text-center px-2 py-1 font-medium">Xero</th>
                          <th className="text-right px-2 py-1 font-medium">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.corrections.slice(0, 20).map((c, i) => (
                          <tr key={i} className="border-b last:border-0">
                            <td className="px-2 py-1 font-mono">{c.invoice_number || '-'}</td>
                            <td className="px-2 py-1">{c.supplier_client_name || '-'}</td>
                            <td className="px-2 py-1">{c.entity_id || '-'}</td>
                            <td className="px-2 py-1 text-center">
                              <Badge variant="destructive" className="text-[10px]">{c.stored_currency || '(none)'}</Badge>
                            </td>
                            <td className="px-2 py-1 text-center">
                              <Badge variant="default" className="text-[10px]">{c.xero_currency}</Badge>
                            </td>
                            <td className="px-2 py-1 text-right font-mono">
                              {c.amount != null ? Number(c.amount).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Amount flags */}
                {preview.amount_flags?.length > 0 && (
                  <div>
                    <div className="flex items-center gap-1 mb-1">
                      <AlertTriangle className="h-3 w-3 text-red-600" />
                      <h5 className="text-xs font-medium text-red-600">
                        Amount Mismatches (skipped, manual review required)
                      </h5>
                    </div>
                    <div className="border border-red-200 rounded overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b bg-red-50">
                            <th className="text-left px-2 py-1 font-medium">Invoice #</th>
                            <th className="text-right px-2 py-1 font-medium">Stored</th>
                            <th className="text-right px-2 py-1 font-medium">Xero Total</th>
                            <th className="text-left px-2 py-1 font-medium">Reason</th>
                          </tr>
                        </thead>
                        <tbody>
                          {preview.amount_flags.map((f, i) => (
                            <tr key={i} className="border-b last:border-0">
                              <td className="px-2 py-1 font-mono">{f.invoice_number || '-'}</td>
                              <td className="px-2 py-1 text-right font-mono">{f.stored_amount}</td>
                              <td className="px-2 py-1 text-right font-mono">{f.xero_total}</td>
                              <td className="px-2 py-1 text-red-600">{f.reason}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Apply results */}
        {applyResult && (
          <div className={`p-3 rounded text-xs border ${applyResult.error ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
            {applyResult.error ? (
              <p className="text-red-600">{applyResult.error}</p>
            ) : (
              <div className="space-y-1">
                <p><strong>Applied:</strong> {applyResult.applied} corrections (run_id: {applyResult.run_id})</p>
                <p><strong>Skipped:</strong> {applyResult.skipped_already_correct} already correct, {applyResult.skipped_amount_flag} amount flags</p>
                {applyResult.applied_samples?.length > 0 && (
                  <div className="mt-1">
                    <span className="font-medium">Samples:</span>
                    {applyResult.applied_samples.map((s, i) => (
                      <span key={i} className="ml-1 font-mono">
                        {s.invoice_number || s.id}: {s.old_currency || '(none)'} {'->'} {s.new_currency}
                        {i < applyResult.applied_samples.length - 1 ? ',' : ''}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
